import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	TextField,
} from "@material-ui/core";

import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { updateOfferProduct } from "../../../api/offerProduct";
import {
	calculateMinutesFromTime,
	formatInstallationTime,
} from "../../../utils/helpers";
import { alertError, alertSuccess } from "../../../utils/logger";
import LoadingDialog from "./LoadingDialog";

function getEmptyOfferProduct() {
	return {
		oferta_id: null,
		revision_num: 0,
		capitulo_id: null,
		partida_id: null,
		producto_id: null,

		descripcion: null,
		referencia: null,
		plantilla_producto_original_id: null,

		tipo_producto_id: null,
		tipo_unidad_id: null,
		def_unidades: null,
		def_factor: null,
		def_precio_compra: null,
		def_descuento_proveedor: null,
		def_tiempo_mano_de_obra: null,
		es_mano_de_obra: false,
		multiplicable: true,

		imputar_tiempo_a_mano_de_obra_interna: false,
		imputar_material_a_subcontrata: false,
	};
}

const EditOfferProductDialog = (props) => {
	const { title, open, setOpen, data, onSave, isSubcontrataMixta } = props;

	const [loading, setLoading] = useState(false);

	const [offerProduct, setOfferProduct] = useState({
		...getEmptyOfferProduct(),
		...data,
	});
	const [timeInput, setTimeInput] = useState("00:00:00");

	const normalizeTimeInputValue = (value) => {
		const normalizedValue = String(value ?? "").trim();
		if (!normalizedValue) {
			return "";
		}

		const totalSeconds = calculateMinutesFromTime(normalizedValue);
		if (totalSeconds === null) {
			return null;
		}

		return formatInstallationTime(totalSeconds);
	};

	useEffect(() => {
		if (open) {
			setOfferProduct({
				...getEmptyOfferProduct(),
				...data,
				multiplicable: data?.multiplicable ?? true,

				// Flags: solo aplican en subcontrata mixta
				imputar_tiempo_a_mano_de_obra_interna: data?.es_mano_de_obra ? true : isSubcontrataMixta
					? (data?.imputar_tiempo_a_mano_de_obra_interna ?? false)
					: false,
				imputar_material_a_subcontrata: data?.es_mano_de_obra ? false : isSubcontrataMixta
					? (data?.imputar_material_a_subcontrata ?? false)
					: false,
			});
			setTimeInput(formatInstallationTime(data?.def_tiempo_mano_de_obra));
		}
	}, [open, data, isSubcontrataMixta]);

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	function saveOfferProduct() {
		const validateProduct = () => {
			let error = "";

			if (!offerProduct?.descripcion) {
				error = "La descripción es obligatoria.";
			} else if (!offerProduct?.referencia) {
				error = "La referencia es obligatoria.";
			} else if (!offerProduct?.def_factor || Number(offerProduct.def_factor) < 1) {
				error = "El factor mínimo es 1.";
			}

			if (!error && calculateMinutesFromTime(timeInput) === null) {
				error = "El tiempo debe tener formato hh:mm:ss.";
			}

			if (error) {
				alertError({
					error: error,
				});
				return false;
			}

			return true;
		};

		if (!validateProduct()) {
			return;
		}

		setLoading(true);
		const payload = {
			...offerProduct,
			def_tiempo_mano_de_obra: calculateMinutesFromTime(timeInput),
			def_unidades: offerProduct.es_mano_de_obra ? 1 : offerProduct.def_unidades,
			def_factor: offerProduct.es_mano_de_obra ? 1 : offerProduct.def_factor,
			def_precio_compra: offerProduct.es_mano_de_obra ? 0 : offerProduct.def_precio_compra,
			def_descuento_proveedor: offerProduct.es_mano_de_obra ? 0 : offerProduct.def_descuento_proveedor,
			imputar_tiempo_a_mano_de_obra_interna: offerProduct.es_mano_de_obra ? true : isSubcontrataMixta
				? (offerProduct?.imputar_tiempo_a_mano_de_obra_interna ?? false)
				: false,
			imputar_material_a_subcontrata: offerProduct.es_mano_de_obra ? false : isSubcontrataMixta
				? (offerProduct?.imputar_material_a_subcontrata ?? false)
				: false,
		};
		updateOfferProduct(
			payload?.producto_id,
			payload,
			loggedUser?.accessToken
		)
			.then((res) => {
				if (res.status === 200 || res.status === 204) {
					setLoading(false);
					alertSuccess({
						title: "¡Guardado!",
						customMessage: "Cambios guardados con éxito",
					});
					onSave(res.data);
					setOpen(false);
					setOfferProduct(getEmptyOfferProduct());
				}
			})
			.catch((error) => {
				setLoading(false);
				alertError({
					error: error,
					customMessage: "No se han podido guardar los cambios",
				});
			});
	}

	//Modificación FerBcn (04/12/2025) -> El "Factor" NUNCA puede ser inferior a 1
	const handleChange = (element) => (event) => {
		let value = event.target.value;

		// Factor: mínimo 1 siempre
		if (element === "def_factor") {
			const num = Number(value);
			if (Number.isNaN(num) || num < 1) {
				value = 1;
			} else {
				value = num;
			}
		}

		setOfferProduct({ ...offerProduct, [element]: value });
	};


	return (
		<>
			<Dialog
				fullWidth={true}
				maxWidth="md"
				open={open}
				onClose={() => {
					setOpen(false);
				}}
				aria-labelledby="table-dialog"
				className="backdrop"
			>
				<DialogTitle id="table-dialog">{title}</DialogTitle>
				<DialogContent>
					<div className="row d-flex align-items-center">
						<div className="col-md-4">
							<TextField
								label="Descripción"
								value={offerProduct.descripcion}
								onChange={handleChange("descripcion")}
								InputLabelProps={{
									shrink: true,
								}}
								margin="normal"
								variant="outlined"
								required
							/>
						</div>
						<div className="col-md-4">
							<TextField
								label="Referencia"
								value={offerProduct.referencia}
								onChange={handleChange("referencia")}
								InputLabelProps={{
									shrink: true,
								}}
								margin="normal"
								variant="outlined"
								required
							/>
						</div>
						<div className="col-md-4">
							<TextField
								label="Precio compra (€)"
								value={offerProduct.es_mano_de_obra ? 0 : offerProduct.def_precio_compra}
								onChange={handleChange("def_precio_compra")}
								disabled={offerProduct.es_mano_de_obra}
								helperText={offerProduct.es_mano_de_obra ? "Coste/hora de la oferta" : undefined}
								InputLabelProps={{
									shrink: true,
								}}
								InputProps={{
									inputProps: {
										min: 0,
									},
								}}
								type="number"
								margin="normal"
								variant="outlined"
								required
							/>
						</div>
					</div>
					<div className="row d-flex align-items-center">
						<div className="col-md-3">
							<TextField
								label="Unidades"
								value={offerProduct.es_mano_de_obra ? 1 : offerProduct.def_unidades}
								onChange={handleChange("def_unidades")}
								disabled={offerProduct.es_mano_de_obra}
								InputLabelProps={{
									shrink: true,
								}}
								InputProps={{
									inputProps: {
										min: 0,
                    step: 0.01,
									},
								}}
								type="number"
								margin="normal"
								variant="outlined"
								required
							/>
						</div>
						<div className="col-md-3">
							<TextField
								label="Factor (%)"
								value={offerProduct.es_mano_de_obra ? 1 : offerProduct.def_factor}
								onChange={handleChange("def_factor")}
								disabled={offerProduct.es_mano_de_obra}
								InputLabelProps={{
									shrink: true,
								}}
								InputProps={{
									inputProps: {
										min: 1,
									},
								}}
								type="number"
								margin="normal"
								variant="outlined"
								required
							/>
						</div>

						<div className="col-md-3">
							<TextField
								label="Descuento proveedor (%)"
								value={offerProduct.es_mano_de_obra ? 0 : offerProduct.def_descuento_proveedor}
								onChange={handleChange(
									"def_descuento_proveedor"
								)}
								InputLabelProps={{
									shrink: true,
								}}
								InputProps={{
									inputProps: {
										min: 0,
									},
								}}
								type="number"
								disabled={offerProduct.es_mano_de_obra}
								margin="normal"
								variant="outlined"
								required
							/>
						</div>
						<div className="col-md-3">
							<TextField
								label="Tiempo (hh:mm:ss)"
								value={timeInput}
								onChange={(event) => setTimeInput(event.target.value)}
								onBlur={() => {
									const normalizedValue = normalizeTimeInputValue(timeInput);
									if (normalizedValue !== null) {
										setTimeInput(normalizedValue);
									}
								}}
								InputLabelProps={{
									shrink: true,
								}}
								InputProps={{
									inputProps: {
										placeholder: "hh:mm:ss",
									},
								}}
								margin="normal"
								variant="outlined"
								required
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-12 d-flex align-items-center flex-wrap">
							<FormControlLabel
								control={
									<Checkbox
										checked={offerProduct?.multiplicable ?? true}
										onChange={(e) =>
											setOfferProduct({
												...offerProduct,
												multiplicable: e.target.checked,
											})
										}
										color="primary"
									/>
								}
								label="Multiplicable"
							/>

							{isSubcontrataMixta && !offerProduct.es_mano_de_obra && (
								<FormControlLabel
									className="ml-4"
									control={
										<Checkbox
											checked={
												offerProduct?.imputar_tiempo_a_mano_de_obra_interna ??
												false
											}
											onChange={(e) =>
												setOfferProduct({
													...offerProduct,
													imputar_tiempo_a_mano_de_obra_interna:
														e.target.checked,
												})
											}
											color="primary"
										/>
									}
									label="Imputar tiempo a mano de obra interna"
								/>
							)}

							{isSubcontrataMixta && !offerProduct.es_mano_de_obra && (
								<FormControlLabel
									className="ml-4"
									control={
										<Checkbox
											checked={
												offerProduct?.imputar_material_a_subcontrata ?? false
											}
											onChange={(e) =>
												setOfferProduct({
													...offerProduct,
													imputar_material_a_subcontrata: e.target.checked,
												})
											}
											color="primary"
										/>
									}
									label="Imputar material a subcontrata"
								/>
							)}
						</div>
					</div>


				</DialogContent>
				<DialogActions>
					<Button
						className="mr-2"
						onClick={() => {
							setOpen(false);
							setOfferProduct(getEmptyOfferProduct());
						}}
						variant="outlined"
					>
						Cancelar
					</Button>
					<Button
						onClick={() => {
							saveOfferProduct();
						}}
						variant="outlined"
						color="primary"
					>
						Guardar
					</Button>
					<br />
				</DialogActions>
			</Dialog>
			<LoadingDialog open={loading} />
		</>
	);
};
export default EditOfferProductDialog;
